import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Quote, Star } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Testimonials = () => {
  const testimonials = [
    {
      id: 1,
      name: 'Mr Rajan Singh',
      position: 'MD, Annu Projects ltd.',
      text: 'The attention to detail and craftsmanship displayed by this team is extraordinary. They transformed our commercial space while keeping everything on schedule.',
      image: '/assets/sketch.jpg',
      rating: 4.5,
      project: 'Commercial Complex Development',
      date: 'March 2024'
    },
    {
      id: 2,
      name: 'Mr Mukesh',
      position: 'One8',
      text: 'Their expertise in modern construction techniques combined with traditional values makes them stand out. The results exceeded our expectations.',
      image: '/assets/sketch.jpg',
      rating: 5,
      project: 'Luxury Villa Construction',
      date: 'January 2024'
    },
    {
      id: 3,
      name: 'Mr Saurav Sharma',
      position: 'Property Owner',
      text: 'From foundation to finish, their work quality is impeccable. The teams communication and professionalism made the entire process smooth.',
      image: '/assets/sketch.jpg',
      rating: 4.5,
      project: 'Residential Complex',
      date: 'February 2024'
    },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);
    return () => clearInterval(interval);
  }, [currentIndex]);

  const handleTouchStart = (e) => {
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStartX - touchEndX > 50) {
      nextSlide();
    }
    if (touchEndX - touchStartX > 50) {
      prevSlide();
    }
  };

  const nextSlide = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
      setTimeout(() => setIsAnimating(false), 500);
    }
  };

  const prevSlide = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
      setTimeout(() => setIsAnimating(false), 500);
    }
  };

  return (
    <div id="testimonials" className="relative bg-gradient-to-b from-gray-50 to-white py-20 overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent 
              bg-gradient-to-r from-blue-600 to-blue-800"
          >
            Client Testimonials
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-gray-600 max-w-3xl mx-auto"
          >
            Hear what our valued clients have to say about their experience working with us
          </motion.p>
        </div>

        <div className="relative px-4 md:px-12"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="relative bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-20 bg-gradient-to-r from-blue-600 to-blue-800" />
            
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5 }}
                className="relative pt-16 pb-12 px-6 md:px-12"
              >
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="md:w-1/3 flex flex-col items-center text-center">
                    <div className="relative">
                      <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-lg">
                        <img
                          src={testimonials[currentIndex].image}
                          alt={testimonials[currentIndex].name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <Quote className="absolute -bottom-2 -right-2 w-8 h-8 text-blue-600 bg-white rounded-full p-1" />
                    </div>
                    <h4 className="mt-4 text-xl font-bold text-gray-900">
                      {testimonials[currentIndex].name}
                    </h4>
                    <p className="text-blue-600 font-medium">
                      {testimonials[currentIndex].position}
                    </p>
                    <div className="flex items-center gap-1 mt-2">
                      {[...Array(Math.floor(testimonials[currentIndex].rating))].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-amber-400 fill-current" />
                      ))}
                      {testimonials[currentIndex].rating % 1 !== 0 && (
                        <Star className="w-5 h-5 text-amber-400 fill-current opacity-50" />
                      )}
                    </div>
                  </div>

                  <div className="md:w-2/3">
                    <div className="bg-blue-50 rounded-2xl p-6 relative">
                      <Quote className="absolute -top-3 -left-3 w-8 h-8 text-blue-600 transform -rotate-12" />
                      <p className="text-gray-700 text-lg md:text-xl leading-relaxed italic">
                        "{testimonials[currentIndex].text}"
                      </p>
                    </div>
                    <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                      <span>Project: {testimonials[currentIndex].project}</span>
                      <span>{testimonials[currentIndex].date}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Buttons */}
          <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 flex justify-between pointer-events-none">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={prevSlide}
              className="pointer-events-auto p-3 rounded-full bg-white shadow-lg hover:bg-gray-50 
                transition-all duration-300 -translate-x-1/2"
            >
              <ChevronLeft className="w-6 h-6 text-blue-600" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={nextSlide}
              className="pointer-events-auto p-3 rounded-full bg-white shadow-lg hover:bg-gray-50 
                transition-all duration-300 translate-x-1/2"
            >
              <ChevronRight className="w-6 h-6 text-blue-600" />
            </motion.button>
          </div>
        </div>

        {/* Dots indicator */}
        <div className="flex justify-center mt-8 space-x-2">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                currentIndex === index
                  ? 'bg-blue-600 w-8'
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Testimonials;